import React from 'react'
import { DSColors, DSSizes } from '../../common/types'

import styles from './Button.module.scss'
import classy from 'shared/utils/classy'

export type ButtonType = 'button' | 'submit' | 'reset'
export type ButtonSize = Extract<
  DSSizes,
  '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
>
export type ButtonColor = Extract<
  DSColors,
  'primary' | 'error' | 'warning' | 'success' | 'secondary-1'
>
export type ButtonAppearance = 'primary' | 'secondary' | 'tertiary' | 'inline'

export interface ButtonProps {
  form?: string
  size?: ButtonSize

  /**
   * This is the html attribute "type" of the button
   */
  type?: ButtonType

  /**
   * Color affects the accent color used to style the button (background, border, text, etc.)
   * - 'primary' - black (default)
   * - 'error' - red
   */
  color?: ButtonColor
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>
  children: React.ReactNode
  style?: React.CSSProperties
  disabled?: boolean
  readonly?: boolean
  loading?: boolean
  tabIndex?: number
  iconOnly?: boolean

  /**
   * The standard html title attribute
   */
  title?: string

  /**
   * Sets the button to the width of the container
   */
  fullWidth?: boolean

  /**
   * This changes the style of the button, it does not affect the color or context that the button would be used in
   * - 'primary' the standard filled button
   * - 'secondary' is a reversed, outline-button appearance
   * - 'tertiary' is a muted borderless button
   *
   * to change the context (warning, error, etc.) use the color prop
   */
  appearance?: ButtonAppearance
  /**
   * Standard behavior is to override the size of svg icons based on the size of the button. Pass false if you want to
   * keep the size passed to the icon component
   */
  overrideIconSize?: boolean
  noBorderRadius?: boolean
  'data-testid'?: string
  'aria-expanded'?: boolean
  'aria-label'?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      form,
      size = 'md',
      type = 'button',
      color = 'primary',
      onClick,
      onKeyDown,
      title,
      disabled = false,
      readonly = false,
      loading = false,
      children,
      tabIndex,
      iconOnly,
      fullWidth,
      appearance = 'primary',
      style,
      overrideIconSize = true,
      noBorderRadius = false,
      'data-testid': dataTestId,
      'aria-expanded': ariaExpanded,
      'aria-label': ariaLabel,
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        form={form}
        type={type}
        title={title}
        onClick={onClick}
        onKeyDown={onKeyDown}
        disabled={disabled || loading}
        tabIndex={tabIndex}
        style={style}
        className={classy(
          styles.Button,
          styles[`Button--${size}`],
          styles[`Button--${appearance}`],
          styles[`Button--${appearance}--${color}`],
          overrideIconSize && styles[`Button--${size}--override-icon-size`],
          iconOnly && styles[`Button--${size}--icon-only`],
          fullWidth && styles['Button--full-width'],
          loading && styles['Button--loading'],
          readonly && styles['Button--readonly'],
          noBorderRadius && styles['Button--no-border-radius'],
        )}
        data-testid={dataTestId ? dataTestId : 'button'}
        aria-expanded={ariaExpanded}
        aria-label={ariaLabel}
      >
        {children}
      </button>
    )
  },
)

export default Button
